import { useState } from "react";
import { FileDown, Send, Briefcase, Wrench, Landmark, Code, Home, Users, Sparkles } from "lucide-react";
import DashboardShell from "../../components/shell/DashboardShell";
import api from "../../lib/api";
import FlyerLeaderboard from "../../components/FlyerLeaderboard";
import { toast } from "sonner";

const FLYERS = [
  {
    type: "sales",
    icon: Sparkles,
    title: "Sales one-pager",
    description: "The all-purpose flyer to email a prospective customer. Pricing table + 6 key features + founder photo + CTA.",
    audience: "Prospective customers (cold outreach, nurture sequences)",
    accent: "emerald",
  },
  {
    type: "investor-pitch",
    icon: Briefcase,
    title: "Investor pitch",
    description: "Design-heavy 1-pager aimed at sponsors / LPs. Big headline, three metric callouts, four outcome bullets.",
    audience: "Multi-LLC sponsors, syndicators, fund managers",
    accent: "indigo",
  },
  {
    type: "internal-spec",
    icon: Code,
    title: "Internal product spec",
    description: "Engineering / eval team breakdown. Architecture, AI fallback chain, all six core capabilities, no marketing fluff.",
    audience: "Eval teams, IT decision-makers, in-house engineers",
    accent: "slate",
  },
  {
    type: "tenant",
    icon: Home,
    title: "Tenant flyer",
    description: "Hands to your tenants — explains the tenant portal: pay rent online, request maintenance, lease access.",
    audience: "Tenants on a property using RealtyVault",
    accent: "rose",
  },
  {
    type: "vendor",
    icon: Wrench,
    title: "Vendor flyer",
    description: "Hands to your vendors — explains the Vendor Portal: magic-link login, fast invoice submission, ACH payouts.",
    audience: "Plumbers, contractors, cleaning crews, etc.",
    accent: "amber",
  },
  {
    type: "lender",
    icon: Landmark,
    title: "Lender / CPA flyer",
    description: "Pitches the Lender Package as a referral channel — what's inside, why it's faster, and how to get a 30-day Pro license.",
    audience: "Commercial banks, mortgage brokers, real-estate CPAs",
    accent: "blue",
  },
];

const ACCENT_CLASSES = {
  emerald: "bg-emerald-600",
  indigo: "bg-indigo-600",
  slate: "bg-slate-700",
  rose: "bg-rose-600",
  amber: "bg-amber-500",
  blue: "bg-blue-600",
};

export default function AdminMarketing() {
  const [downloading, setDownloading] = useState({});

  const downloadFlyer = async (flyerType) => {
    const prospect = window.prompt(
      `Personalize "${FLYERS.find(f => f.type === flyerType).title}" for which prospect?\n(adds a "Built for {name}" badge — leave blank for a generic flyer)`,
      ""
    );
    if (prospect === null) return;
    setDownloading((s) => ({ ...s, [flyerType]: true }));
    try {
      const params = {};
      if (prospect.trim()) params.prospect = prospect.trim();
      const res = await api.get(`/admin/marketing/flyer/${flyerType}.pdf`, {
        responseType: "blob",
        params,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (prospect.trim() || "")
        .replace(/[^a-z0-9-_ ]/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 40);
      const baseName = `RealtyVault-${flyerType.replace(/-/g, "_")}`;
      a.download = safe ? `${baseName}-${safe}.pdf` : `${baseName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${baseName} downloaded`, { duration: 2500 });
    } catch {
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading((s) => ({ ...s, [flyerType]: false }));
    }
  };

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="mb-8">
          <div className="overline text-emerald-700">Super Admin</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
            Marketing kit
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Downloadable PDFs for every audience — sales, sponsors, tenants, vendors, lenders.
            Each flyer pulls live pricing so they can never go stale, and each accepts a "Built for {`{prospect}`}" badge.
          </p>
        </div>

        <FlyerLeaderboard />

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="marketing-flyers-grid"
        >
          {FLYERS.map((f) => {
            const Icon = f.icon;
            const isDl = !!downloading[f.type];
            return (
              <div
                key={f.type}
                className="bg-white border border-slate-200 hover:border-slate-900 rounded-sm p-5 flex flex-col transition-colors"
                data-testid={`marketing-flyer-card-${f.type}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-sm ${ACCENT_CLASSES[f.accent]} text-white flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>
                      {f.title}
                    </h3>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5 font-semibold">
                      {f.audience}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 flex-1">{f.description}</p>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => downloadFlyer(f.type)}
                    disabled={isDl}
                    className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 hover:border-slate-900 text-slate-700 hover:text-slate-900 disabled:opacity-50 disabled:cursor-wait bg-white px-3 py-2 rounded-sm transition-colors"
                    data-testid={`marketing-flyer-download-${f.type}`}
                  >
                    {isDl ? (
                      <>
                        <Send className="w-4 h-4 animate-pulse" /> Generating…
                      </>
                    ) : (
                      <>
                        <FileDown className="w-4 h-4" /> Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 p-4 bg-slate-50 border border-slate-200 rounded-sm flex items-start gap-3">
          <Users className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-600">
            <strong className="text-slate-900">Tip:</strong> personalising a flyer with the
            prospect's company name not only looks better — it also feeds the leaderboard above so
            you can see which prospects are getting the most outreach this week.
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
